import { useState } from "react";
import { MapPin, ChevronDown } from "lucide-react";
import { Link } from "react-router-dom";
import logo from "@/assets/1-wood-floors-logo.webp";
import FooterBrand from "@/components/footer/FooterBrand";
import FooterLinks from "@/components/footer/FooterLinks";
import FooterServiceAreas from "@/components/footer/FooterServiceAreas";
import FooterBrands from "@/components/footer/FooterBrands";
import FooterContact from "@/components/footer/FooterContact";
import FooterBottomBar from "@/components/footer/FooterBottomBar";

/* ── Mobile disclosure section ── */
const Disclosure = ({ title, children }: { title: string; children: React.ReactNode }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ borderBottom: "1px solid var(--footer-border)" }}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-4 text-left"
      >
        <span
          className="text-[11px] font-semibold tracking-[0.08em] uppercase"
          style={{ color: "var(--footer-text-tertiary)" }}
        >
          {title}
        </span>
        <ChevronDown
          className="w-4 h-4 transition-transform duration-300"
          style={{
            color: "var(--footer-text-tertiary)",
            transform: open ? "rotate(180deg)" : "rotate(0)",
          }}
        />
      </button>
      <div
        className="overflow-hidden transition-all duration-300 ease-in-out"
        style={{ maxHeight: open ? "600px" : "0", opacity: open ? 1 : 0 }}
      >
        <div className="pb-6">{children}</div>
      </div>
    </div>
  );
};

const SiteFooter = () => (
  <footer role="contentinfo" style={{ backgroundColor: "var(--footer-bg)" }}>
    {/* ── Map Banner ── */}
    <div className="relative w-full" style={{ height: "260px" }}>
      <iframe
        title="1 Wood Floors showroom  Livonia, MI"
        src="https://www.google.com/maps/embed/v1/place?key=AIzaSyCXeD0xS7CsO0guokvYyZn7ymMsiXbCvYc&q=1+Wood+Floors,33475+W+Seven+Mile+Rd,Livonia,MI+48152"
        width="100%"
        height="100%"
        style={{ border: 0, position: "absolute", top: 0, left: 0, width: "100%", height: "100%" }}
        className="grayscale-[85%] contrast-[1.2] brightness-[0.85] saturate-[0.3]"
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        allowFullScreen
      />
      <div
        className="absolute top-4 left-4 rounded-full px-4 py-2 flex items-center gap-2 z-10"
        style={{ backgroundColor: "rgba(13,13,13,0.85)", backdropFilter: "blur(12px)" }}
      >
        <MapPin className="w-3.5 h-3.5" style={{ color: "var(--footer-gold)" }} />
        <span className="text-[10px] font-semibold tracking-[0.12em] uppercase" style={{ color: "var(--footer-text-primary)", opacity: 0.8 }}>
          Livonia Showroom
        </span>
      </div>
      <div
        className="absolute bottom-0 left-0 right-0 h-16 pointer-events-none"
        style={{ background: "linear-gradient(to top, var(--footer-bg), transparent)" }}
      />
    </div>

    {/* ── DESKTOP: 5-column grid (xl+) ── */}
    <div
      className="hidden xl:block max-w-[1200px] mx-auto"
      style={{ padding: "64px 40px 0", borderTop: "1px solid var(--footer-border)" }}
    >
      <div className="grid gap-12" style={{ gridTemplateColumns: "1.4fr 1fr 1.6fr 1fr 1.4fr" }}>
        <FooterBrand />
        <FooterLinks />
        <FooterServiceAreas />
        <FooterBrands />
        <FooterContact />
      </div>
    </div>

    {/* ── TABLET: 2-row layout (md to xl) ── */}
    <div
      className="hidden md:block xl:hidden max-w-[1200px] mx-auto"
      style={{ padding: "48px 40px 0", borderTop: "1px solid var(--footer-border)" }}
    >
      <div className="grid grid-cols-3 gap-10 pb-10" style={{ borderBottom: "1px solid var(--footer-border)" }}>
        <FooterBrand />
        <FooterLinks />
        <FooterBrands />
      </div>
      <div className="grid grid-cols-2 gap-10 pt-10">
        <FooterServiceAreas />
        <FooterContact />
      </div>
    </div>

    {/* ── MOBILE: Stacked (< md) ── */}
    <div
      className="md:hidden"
      style={{ padding: "48px 24px 0", borderTop: "1px solid var(--footer-border)" }}
    >
      {/* Logo + tagline */}
      <div className="mb-10">
        <Link to="/">
          <img loading="lazy" width="227" height="154" src={logo} alt="1 Wood Floors" className="h-[48px] w-auto brightness-[1.05]" />
        </Link>
        <p
          className="text-[13px] leading-[1.7] mt-3 max-w-[280px]"
          style={{ color: "var(--footer-text-secondary)" }}
        >
          Michigan's most credentialed hardwood flooring manufacturer and retailer. Four NWFA Certifications: Installer, Sand and Finish, Inspector, and Sales Advisor.
        </p>
      </div>

      {/* Open sections */}
      <div className="mb-10">
        <FooterLinks />
      </div>

      {/* Collapsible */}
      <Disclosure title="Service Areas">
        <FooterServiceAreas />
      </Disclosure>
      <Disclosure title="Brands">
        <FooterBrands />
      </Disclosure>

      {/* Contact always visible */}
      <div className="pt-10 pb-4">
        <FooterContact />
      </div>
    </div>

    {/* ── Bottom Bar (64px margin from content) ── */}
    <div className="mt-16">
      <FooterBottomBar />
    </div>
  </footer>
);

export default SiteFooter;
